import { lazy } from "preact/compat";

const identity = (e: unknown) => e;
const uriOrRelativePathRegex = /"((\w+:(\/?\/?))?[^\s]+)"/;
function parseModulePathFromImporterBody(
  importer: () => unknown
): string | null {
  const fnString = importer.toString();
  const match = fnString.match(uriOrRelativePathRegex);
  if (!match) return null;
  return match.filter(identity)[1];
}

type UrlStrategy = (error: Error, importer: () => unknown) => string | null;
export type StrategyName =
  | "PARSE_ERROR_MESSAGE"
  | "PARSE_IMPORTER_FUNCTION_BODY";

const strategies: Record<StrategyName, UrlStrategy> = {
  /** This only works in Chromium browsers, as other engines (like Firefox) do not carry the module url in the error message */
  PARSE_ERROR_MESSAGE: (error: Error) => {
    // this assumes that the exception will contain this specific text with the url of the module
    // if not, the url will not be able to parse and we'll get an error on that
    // eg. "Failed to fetch dynamically imported module: https://example.com/assets/Home.tsx"
    const urlAsString = error.message
      .replace("Failed to fetch dynamically imported module: ", "")
      .trim();

    const url = new URL(urlAsString);
    return url.href;
  },
  /** Should work in most browsers */
  PARSE_IMPORTER_FUNCTION_BODY: (_: Error, importer: () => unknown) => {
    return parseModulePathFromImporterBody(importer);
  },
};

const defaultOpts = {
  strategy: "PARSE_IMPORTER_FUNCTION_BODY" as const,
};

export const lazyWithRetries: typeof lazy = (importer) => {
  const retryImport = async () => {
    try {
      return await importer();
    } catch (error: unknown) {
      const { strategy } = defaultOpts;

      const modulePath = strategies[strategy](error as Error, importer);

      if (!modulePath) {
        throw error;
      }

      // retry 5 times with 2 second delay and backoff factor of 2 (2, 4, 8, 16, 32 seconds)
      for (let i = 0; i < 5; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** i));
        const cacheBustedPath = `${modulePath}?t=${+new Date()}`;

        try {
          return await import(/* @vite-ignore */ cacheBustedPath);
        } catch (e) {
          console.log("retrying import");
        }
      }
      throw error;
    }
  };
  return lazy(retryImport);
};
